import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AnalyticsSection } from "../components/AnalyticsSection";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";




import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Heart,
  Loader2,
  Sparkles,
  EyeOff,
  Printer,
  Search,
  X,
  ArrowUpDown,
} from "lucide-react";
import { apiClient } from "../lib/apiClient";
import { MarkAnsweredDialog } from "../components/MarkAnsweredDialog";
import { EditPrayerDialog } from "../components/EditPrayerDialog";
import { PREDEFINED_TAGS } from "../components/TagSelector";
import { DateRangeFilter } from "../components/DateRangeFilter";

import PrintPreview from "../components/PrintPreview";
import { PrayerTabContent } from "../components/PrayerTabContent";
import { ProfileDialog } from "../components/ProfileDialog";
import { GroupsSidebar } from "../components/GroupsSidebar";
import { BulkActionsBar } from "../components/BulkActionsBar";
import { HelpDialog } from "../components/HelpDialog";
import { DashboardNav } from "../components/DashboardNav";
import { DashboardStats } from "../components/DashboardStats";
import { NewPrayerDialog } from "../components/NewPrayerDialog";

const formatDateValue = (date) => {
  if (!date) return null;
  return typeof date === 'string' ? date : date.toISOString();
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [prayers, setPrayers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState({ total_prayers: 0, answered_prayers: 0, active_prayers: 0, group_count: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  
  // Dialog states
  const [newPrayerOpen, setNewPrayerOpen] = useState(false);
  const [editPrayerOpen, setEditPrayerOpen] = useState(false);
  const [markAnsweredOpen, setMarkAnsweredOpen] = useState(false);
  const [selectedPrayer, setSelectedPrayer] = useState(null);
  
  // Search & Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [filterGroup, setFilterGroup] = useState("all");
  const [sortMode, setSortMode] = useState("newest");
  const [filterTag, setFilterTag] = useState("all");
  const [dateStart, setDateStart] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);

  // Bulk selection state
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedPrayerIds, setSelectedPrayerIds] = useState([]);
  
  // Mark as Answered form
  const [answeredForm, setAnsweredForm] = useState({
    result: "",
    date_answered: null
  });
  
  // Form states
  const [prayerForm, setPrayerForm] = useState({
    request: "",
    notes: "",
    requested_by: "",
    date_requested: null,
    result: "",
    date_answered: null,
    is_hidden: false,
    group_id: "",
    tags: []
  });
  const [churches, setChurches] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // handleLogout must be defined before fetchData since fetchData depends on it
  const handleLogout = useCallback(async () => {
    try {
      await apiClient.post("authLogout");
    } catch { /* ignore logout errors */ }
    sessionStorage.removeItem("auth_token");
    localStorage.removeItem("user");
    navigate("/login");
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const [prayersRes, groupsRes, statsRes, churchesRes, analyticsRes] = await Promise.all([
        apiClient.get("prayersIncludeHidden"),
        apiClient.get("groups"),
        apiClient.get("stats"),
        apiClient.get("churches"),
        apiClient.get("analytics")
      ]);
      setPrayers(prayersRes.data);
      setGroups(groupsRes.data);
      setStats(statsRes.data);
      setChurches(churchesRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      } else {
        alert("Failed to load data. Please try refreshing.");
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);  

  useEffect(() => {
    const userData = localStorage.getItem("user");
    
    if (!userData) {
      navigate("/login");
      return;
    }
    
    setUser(JSON.parse(userData));
    fetchData();
  }, [navigate, fetchData]);

  const resetPrayerForm = () => {
    setPrayerForm({
      request: "",
      notes: "",
      requested_by: "",
      date_requested: null,
      result: "",
      date_answered: null,
      is_hidden: false,
      group_id: "",
      tags: []
    });
  };

  const createPrayer = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {
        request: prayerForm.request,
        notes: prayerForm.notes || null,
        requested_by: prayerForm.requested_by || null,
        date_requested: prayerForm.date_requested ? prayerForm.date_requested.toISOString() : null,
        group_id: prayerForm.group_id || null,
        tags: prayerForm.tags.length > 0 ? prayerForm.tags : null
      };
      await apiClient.post("prayers", payload);
      resetPrayerForm();
      setNewPrayerOpen(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create prayer");
    } finally {
      setFormLoading(false);
    }
  };

  const updatePrayer = async (e) => {
    e.preventDefault();
    if (!selectedPrayer) return;
    setFormLoading(true);
    try {
      const payload = {
        request: prayerForm.request,
        notes: prayerForm.notes || null,
        requested_by: prayerForm.requested_by || null,
        date_requested: formatDateValue(prayerForm.date_requested),
        result: prayerForm.result || null,
        date_answered: formatDateValue(prayerForm.date_answered),
        is_hidden: prayerForm.is_hidden,
        tags: prayerForm.tags || []
      };
      await apiClient.patch("prayer", selectedPrayer.id, payload);
      setEditPrayerOpen(false);
      setSelectedPrayer(null);
      resetPrayerForm();
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update prayer");
    } finally {
      setFormLoading(false);
    }
  };

  const markAsAnswered = async (prayer) => {
    setSelectedPrayer(prayer);
    setAnsweredForm({ result: "", date_answered: new Date() });
    setMarkAnsweredOpen(true);
  };

  const submitMarkAsAnswered = async (e) => {
    e.preventDefault();
    if (!selectedPrayer) return;
    setFormLoading(true);
    try {
      await apiClient.patch("prayer", selectedPrayer.id, { 
          is_answered: true, 
          result: answeredForm.result || "Answered",
          date_answered: answeredForm.date_answered ? answeredForm.date_answered.toISOString() : new Date().toISOString()
        });
      setMarkAnsweredOpen(false);
      setSelectedPrayer(null);
      setAnsweredForm({ result: "", date_answered: null });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to mark as answered");
    } finally {
      setFormLoading(false);
    }
  };

  const openPrintPreview = () => {
    setPrintPreviewOpen(true);
  };

  const deletePrayer = async (prayerId) => {
    try {
      await apiClient.delete("prayer", prayerId);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete prayer");
    }
  };

  const handlePrayedFor = async (prayer) => {
    // Optimistic update so the count chip flips immediately. We don't surface
    // server-side errors prominently (this action is fire-and-forget for UX) —
    // a failed POST simply rolls back and re-syncs on the next fetchData.
    setPrayers((prev) =>
      prev.map((p) => (p.id === prayer.id ? { ...p, prayer_count: (p.prayer_count || 0) + 1 } : p)),
    );
    try {
      await apiClient.post("prayerPrayedFor", prayer.id);
    } catch (err) {
      setPrayers((prev) =>
        prev.map((p) => (p.id === prayer.id ? { ...p, prayer_count: prayer.prayer_count || 0 } : p)),
      );
      alert(err.response?.data?.detail || "Failed to record prayer");
    }
  };

  const togglePrayerSelect = (prayerId) => {
    setSelectedPrayerIds((prev) =>
      prev.includes(prayerId) ? prev.filter((id) => id !== prayerId) : [...prev, prayerId]
    );
  };

  const cancelBulkSelect = () => {
    setSelectedPrayerIds([]);
    setBulkSelectMode(false);
  };

  const onBulkCompleted = () => {
    setSelectedPrayerIds([]);
    setBulkSelectMode(false);
    fetchData();
  };

  const openEditPrayer = (prayer) => {
    setSelectedPrayer(prayer);
    setPrayerForm({
      request: prayer.request,
      notes: prayer.notes || "",
      requested_by: prayer.requested_by || "",
      date_requested: prayer.date_requested ? new Date(prayer.date_requested) : null,
      result: prayer.result || "",
      date_answered: prayer.date_answered ? new Date(prayer.date_answered) : null,
      is_hidden: prayer.is_hidden || false,
      group_id: prayer.group_id || "",
      tags: prayer.tags || []
    });
    setEditPrayerOpen(true);
  };

  // Filter prayers based on search, group, tag, and date range
  const filterPrayers = (prayerList) => {
    return prayerList.filter((prayer) => {
      const matchesSearch = searchQuery === "" || 
        prayer.request.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (prayer.requested_by && prayer.requested_by.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (prayer.notes && prayer.notes.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesGroup = filterGroup === "all" || 
        (filterGroup === "personal" && !prayer.group_id) ||
        prayer.group_id === filterGroup;
      
      const matchesTag = filterTag === "all" ||
        (prayer.tags && prayer.tags.includes(filterTag));
      
      const prayerDate = new Date(prayer.date_requested || prayer.created_at);
      const matchesDateStart = !dateStart || prayerDate >= dateStart;
      const matchesDateEnd = !dateEnd || prayerDate <= new Date(dateEnd.getTime() + 86400000);
      
      return matchesSearch && matchesGroup && matchesTag && matchesDateStart && matchesDateEnd;
    });
  };

  // Sort prayers based on sortMode
  const sortPrayers = (prayerList) => {
    const sorted = [...prayerList];
    if (sortMode === "oldest") {
      sorted.sort((a, b) => new Date(a.date_requested || a.created_at) - new Date(b.date_requested || b.created_at));
    } else if (sortMode === "newest") {
      sorted.sort((a, b) => new Date(b.date_requested || b.created_at) - new Date(a.date_requested || a.created_at));
    } else if (sortMode === "requester") {
      sorted.sort((a, b) => (a.requested_by || "").localeCompare(b.requested_by || ""));
    }
    return sorted;
  };

  // Group prayers by requester
  const activePrayers = sortPrayers(filterPrayers(prayers.filter((p) => !p.is_answered && !p.is_hidden)));
  const answeredPrayers = sortPrayers(filterPrayers(prayers.filter((p) => p.is_answered && !p.is_hidden)));
  const hiddenPrayers = sortPrayers(filterPrayers(prayers.filter((p) => p.is_hidden)));

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-light flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sage" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-light noise-bg">
      <DashboardNav
        user={user}
        onProfile={() => setProfileOpen(true)}
        onHelp={() => setHelpOpen(true)}
        onLogout={handleLogout}
      />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome & Stats */}
        <div className="mb-8">
          <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-stone-dark mb-2" data-testid="welcome-message">
            Welcome, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-sm sm:text-base text-stone-dark/60">Here's your prayer journey at a glance</p>
        </div>

        <DashboardStats stats={stats} />

        {/* Analytics Section */}
        {analytics && analytics.total > 0 && (
          <div className="mb-8">
            <AnalyticsSection analytics={analytics} title="Prayer Insights" testIdPrefix="analytics" />
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Prayers Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4 gap-2">
              <h2 className="font-serif text-lg sm:text-xl font-semibold text-stone-dark shrink-0">My Prayers</h2>
              <div className="flex items-center gap-2">
                {bulkSelectMode ? (
                  <BulkActionsBar
                    selectedIds={selectedPrayerIds}
                    onCompleted={onBulkCompleted}
                    onCancel={cancelBulkSelect}
                    testIdPrefix="bulk"
                  />
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full text-stone-dark/60"
                      onClick={() => setBulkSelectMode(true)}
                      data-testid="bulk-select-btn"
                    >
                      Select
                    </Button>
                    <Button 
                  variant="outline" 
                  className="rounded-full"
                  onClick={openPrintPreview}
                  data-testid="print-btn"
                >
                  <Printer className="w-4 h-4" />
                  <span className="ml-2 hidden sm:inline">Print</span>
                </Button>
                <NewPrayerDialog
                  open={newPrayerOpen}
                  onOpenChange={setNewPrayerOpen}
                  prayerForm={prayerForm}
                  setPrayerForm={setPrayerForm}
                  groups={groups}
                  formLoading={formLoading}
                  onSubmit={createPrayer}
                  onClose={resetPrayerForm}
                />
              </>
                )}
              </div>
            </div>

            {/* Search and Filter Bar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-dark/40" />
                <Input
                  placeholder="Search prayers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10 bg-white"
                  data-testid="search-prayers"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-dark/40 hover:text-stone-dark"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Select value={filterGroup} onValueChange={setFilterGroup}>
                <SelectTrigger className="w-full sm:w-48" data-testid="filter-group">
                  <SelectValue placeholder="All prayers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All prayers</SelectItem>
                  <SelectItem value="personal">Personal only</SelectItem>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortMode} onValueChange={setSortMode}>
                <SelectTrigger className="w-full sm:w-48" data-testid="sort-prayers">
                  <ArrowUpDown className="w-4 h-4 mr-2 text-stone-dark/50" />
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Date (Newest First)</SelectItem>
                  <SelectItem value="oldest">Date (Oldest First)</SelectItem>
                  <SelectItem value="requester">By Requester</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tag & Date Range Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Select value={filterTag} onValueChange={setFilterTag}>
                <SelectTrigger className="w-full sm:w-48" data-testid="filter-tag">
                  <SelectValue placeholder="All tags" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tags</SelectItem>
                  {PREDEFINED_TAGS.map((tag) => (
                    <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DateRangeFilter
                startDate={dateStart}
                endDate={dateEnd}
                onStartChange={setDateStart}
                onEndChange={setDateEnd}
                onClear={() => { setDateStart(null); setDateEnd(null); }}
              />
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-4 bg-stone">
                <TabsTrigger value="active" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="active-tab">
                  Active ({activePrayers.length})
                </TabsTrigger>
                <TabsTrigger value="answered" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="answered-tab">
                  Answered ({answeredPrayers.length})
                </TabsTrigger>
                <TabsTrigger value="hidden" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="hidden-tab">
                  Hidden ({hiddenPrayers.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="active" className="space-y-4">
                <PrayerTabContent
                  prayers={activePrayers}
                  emptyIcon={Heart}
                  emptyMessage="No active prayers yet"
                  emptyHint='Click "New Prayer" to add one'
                  sortMode={sortMode}
                  accentColor="#4A6741"
                  onMarkAnswered={markAsAnswered}
                  onEdit={openEditPrayer}
                  onDelete={deletePrayer}
                  onPrayedFor={handlePrayedFor}
                  selectable={bulkSelectMode}
                  selectedIds={selectedPrayerIds}
                  onSelectToggle={togglePrayerSelect}
                />
              </TabsContent>

              <TabsContent value="answered" className="space-y-4">
                <PrayerTabContent
                  prayers={answeredPrayers}
                  emptyIcon={Sparkles}
                  emptyMessage="No answered prayers yet"
                  emptyHint="When prayers are answered, they'll appear here"
                  sortMode={sortMode}
                  accentColor="#D4A373"
                  isAnswered
                  onEdit={openEditPrayer}
                  onDelete={deletePrayer}
                />
              </TabsContent>

              <TabsContent value="hidden" className="space-y-4">
                <PrayerTabContent
                  prayers={hiddenPrayers}
                  emptyIcon={EyeOff}
                  emptyMessage="No hidden prayers"
                  emptyHint="Hidden prayers won't appear in your main list"
                  sortMode={sortMode}
                  accentColor="rgba(0,0,0,0.3)"
                  onEdit={openEditPrayer}
                  onDelete={deletePrayer}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Groups Section */}
          <div>
            <GroupsSidebar groups={groups} churches={churches} onDataRefresh={fetchData} />
          </div>
        </div>
      </main>

      {/* Edit Prayer Dialog */}
      <EditPrayerDialog
        open={editPrayerOpen}
        onOpenChange={(open) => { setEditPrayerOpen(open); if (!open) { setSelectedPrayer(null); resetPrayerForm(); } }}
        prayerForm={prayerForm}
        setPrayerForm={setPrayerForm}
        onSubmit={updatePrayer}
        formLoading={formLoading}
      />

      {/* Mark as Answered Dialog */}
      <MarkAnsweredDialog
        open={markAnsweredOpen}
        onOpenChange={(open) => { setMarkAnsweredOpen(open); if (!open) { setSelectedPrayer(null); setAnsweredForm({ result: "", date_answered: null }); } }}
        selectedPrayer={selectedPrayer}
        answeredForm={answeredForm}
        setAnsweredForm={setAnsweredForm}
        onSubmit={submitMarkAsAnswered}
        formLoading={formLoading}
      />


      {/* Profile Dialog */}
      <ProfileDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        user={user}
        churches={churches}
        onUserUpdate={setUser}
      />

      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />

      {/* Print Preview */}
      <PrintPreview
        isOpen={printPreviewOpen}
        onClose={() => setPrintPreviewOpen(false)}
        prayers={sortPrayers(filterPrayers(prayers.filter(p => !p.is_hidden)))}
        title={`${user?.name}'s Prayer List`}
        showGroupColumn={true}
        groups={groups}
        sortMode={sortMode}
      />
    </div>
  );
}

// Prayer Card Component
